import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  CommandDialog, CommandEmpty, CommandGroup, CommandInput,
  CommandItem, CommandList, CommandSeparator,
} from "@/components/ui/command";
import api, { prs as prsApi } from "@/lib/api";
import { fmtAmt, fmtDate, freshness } from "@/lib/flags";
import {
  LayoutDashboard, FilePlus, FileText, Settings, Database, IndianRupee,
  Hash, FileSearch, User, Building, Receipt, ArrowRight, Sparkles,
} from "lucide-react";

const APP_CODES = [
  { code: "D",  label: "Approval Dashboard",  hint: "Approve / Authorise PRs", icon: LayoutDashboard, to: "/" },
  { code: "M",  label: "My PRs",              hint: "PRs I created",            icon: FileText,        to: "/my-prs" },
  { code: "N",  label: "New PR",              hint: "Draft a new requisition",  icon: FilePlus,        to: "/create" },
  { code: "S",  label: "Savings Tracker",     hint: "Top savings opportunities",icon: IndianRupee,     to: "/?savings=1" },
  { code: "A",  label: "Admin · Logins & Roles", hint: "User & RBAC management", icon: Settings,        to: "/admin" },
  { code: "DB", label: "Admin · DB Console",  hint: "SQL test runner",          icon: Database,        to: "/admin?tab=db" },
];

export default function Fastpath() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState({ prs: [], suppliers: [], employees: [], pos: [] });
  const [busy, setBusy] = useState(false);
  const timer = useRef(null);
  const nav = useNavigate();

  // Global shortcut
  useEffect(() => {
    const handler = (e) => {
      const meta = e.metaKey || e.ctrlKey;
      if (meta && e.key === "/") { e.preventDefault(); setOpen((o) => !o); }
      else if (!meta && e.key === "/" && document.activeElement?.tagName !== "INPUT"
               && document.activeElement?.tagName !== "TEXTAREA"
               && document.activeElement?.getAttribute?.("contenteditable") !== "true") {
        e.preventDefault(); setOpen(true);
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, []);

  // Debounced global search
  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    if (!q.trim()) { setResults({ prs: [], suppliers: [], employees: [], pos: [] }); return; }
    timer.current = setTimeout(async () => {
      setBusy(true);
      try {
        const { data } = await api.get("/search/global", { params: { q, limit: 6 } });
        setResults(data || { prs: [], suppliers: [], employees: [], pos: [] });
      } catch (e) { console.warn("global search failed", e); }
      finally { setBusy(false); }
    }, 220);
    return () => timer.current && clearTimeout(timer.current);
  }, [q]);

  const close = () => { setOpen(false); setQ(""); setResults({ prs: [], suppliers: [], employees: [], pos: [] }); };
  const go = (path) => { close(); nav(path); };
  const openPR = async (voc_no) => {
    // Verify PR exists, then route to dashboard with the PR opened.
    try { await prsApi.get(voc_no); }
    catch { return; }
    close();
    nav(`/?openPR=${voc_no}`);
  };

  // App-code shortcut: typing exact code (D / M / N / A / S / DB) at top
  const exactCode = APP_CODES.find((c) => c.code.toLowerCase() === q.trim().toLowerCase());
  // Numeric-only → treat as PR voc_no
  const numQ = q.trim();
  const numericPR = /^(?:#|pr\s*[#:]?\s*|voc[_\s]*no[:\s]*)?(\d{3,8})$/i.exec(numQ)?.[1];

  return (
    <CommandDialog open={open} onOpenChange={(o) => o ? setOpen(true) : close()}>
      <CommandInput
        value={q}
        onValueChange={setQ}
        placeholder="Fastpath — type code (D / M / N / S), PR no, supplier, employee… or just search"
        data-testid="fastpath-input"
      />
      <CommandList className="max-h-[440px]">
        <CommandEmpty>{busy ? "Searching…" : "No matches. Try a different term."}</CommandEmpty>

        {/* App-codes always shown */}
        <CommandGroup heading="Quick Actions">
          {APP_CODES.map((c) => {
            const Icon = c.icon;
            return (
              <CommandItem
                key={c.code}
                value={`${c.code} ${c.label} ${c.hint}`}
                onSelect={() => go(c.to)}
                data-testid={`fastpath-code-${c.code}`}
              >
                <Icon size={14} className="text-cyan-700 mr-2 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm">{c.label}</div>
                  <div className="text-[10px] text-slate-400 truncate">{c.hint}</div>
                </div>
                <span className="inline-flex items-center justify-center min-w-[24px] h-5 px-1.5 rounded-sm bg-slate-100 border border-slate-200 text-[10px] mono font-semibold text-slate-700 ml-2">
                  {c.code}
                </span>
                {exactCode?.code === c.code && (
                  <ArrowRight size={12} className="ml-1 text-cyan-700" />
                )}
              </CommandItem>
            );
          })}
        </CommandGroup>

        {/* Numeric → "Open PR #N" shortcut */}
        {numericPR && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Direct open">
              <CommandItem
                value={`open pr ${numericPR}`}
                onSelect={() => openPR(numericPR)}
                data-testid="fastpath-open-pr"
              >
                <Hash size={14} className="text-cyan-700 mr-2 shrink-0" />
                <div className="flex-1 text-sm">Open PR #{numericPR}</div>
                <ArrowRight size={12} className="text-cyan-700" />
              </CommandItem>
            </CommandGroup>
          </>
        )}

        {/* PR matches */}
        {results.prs.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading={`PRs (${results.prs.length})`}>
              {results.prs.map((p) => (
                <CommandItem
                  key={`pr-${p.voc_no}`}
                  value={`pr ${p.voc_no} ${p.supplier} ${p.pcode}`}
                  onSelect={() => openPR(p.voc_no)}
                  data-testid={`fastpath-pr-${p.voc_no}`}
                >
                  <FileSearch size={14} className="text-cyan-700 mr-2 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm">
                      <span className="mono font-semibold text-cyan-800">PR #{p.voc_no}</span>
                      <span className="text-slate-400 mx-1">·</span>
                      <span className="truncate">{p.supplier || p.pcode}</span>
                    </div>
                    <div className="text-[10px] text-slate-400 flex items-center gap-2">
                      <span>{p.purc_type}</span>
                      <span>{fmtDate(p.vdate)}</span>
                      <span>₹{fmtAmt(p.amount)}</span>
                      <span className={`px-1 rounded-sm ${p.req_status === "Approved" ? "bg-emerald-100 text-emerald-800" : p.req_status === "Hold" ? "bg-amber-100 text-amber-900" : p.req_status === "Rejected" || p.req_status === "Reject" ? "bg-rose-100 text-rose-800" : "bg-slate-100 text-slate-700"}`}>
                        {p.req_status || "Open"}
                      </span>
                    </div>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}

        {/* Supplier matches */}
        {results.suppliers.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading={`Suppliers (${results.suppliers.length})`}>
              {results.suppliers.map((s) => (
                <CommandItem
                  key={`s-${s.pcode}`}
                  value={`supplier ${s.pcode} ${s.party}`}
                  onSelect={() => go(`/?supplier=${encodeURIComponent(s.pcode)}`)}
                  data-testid={`fastpath-sup-${s.pcode}`}
                >
                  <Building size={14} className="text-cyan-700 mr-2 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm truncate">{s.party}</div>
                    <div className="text-[10px] mono text-slate-400">{s.pcode}</div>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}

        {/* Employee matches */}
        {results.employees.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading={`People (${results.employees.length})`}>
              {results.employees.map((e) => (
                <CommandItem
                  key={`e-${e.emp_no}`}
                  value={`person ${e.uname} ${e.full_name} ${e.emp_no}`}
                  onSelect={() => go(`/?approver=${encodeURIComponent(e.emp_no)}`)}
                  data-testid={`fastpath-emp-${e.emp_no}`}
                >
                  <User size={14} className="text-cyan-700 mr-2 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm truncate">{e.full_name}</div>
                    <div className="text-[10px] mono text-slate-400">{e.uname} · #{e.emp_no}{!e.active && " · inactive"}</div>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}

        {/* PO matches */}
        {results.pos.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading={`POs (${results.pos.length})`}>
              {results.pos.map((o) => {
                const fr = freshness(o.vdate);
                return (
                  <CommandItem
                    key={`po-${o.purc_no}`}
                    value={`po ${o.purc_no} ${o.party}`}
                    onSelect={() => {}}
                    data-testid={`fastpath-po-${o.purc_no}`}
                  >
                    <Receipt size={14} className="text-cyan-700 mr-2 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm">
                        <span className="mono font-semibold text-cyan-800">PO #{o.purc_no}</span>
                        <span className="text-slate-400 mx-1">·</span>
                        <span className="truncate">{o.party}</span>
                      </div>
                      <div className="text-[10px] text-slate-400 flex items-center gap-2">
                        <span>{o.purc_type}</span>
                        {fr && <span className="inline-flex items-center gap-1"><span className={`inline-block w-1.5 h-1.5 rounded-full ${fr.dotClass}`} />{fmtDate(o.vdate)}</span>}
                        <span>₹{fmtAmt(o.amount)}</span>
                      </div>
                    </div>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </>
        )}

        <CommandSeparator />
        <CommandGroup heading="Tip">
          <div className="text-[10px] text-slate-400 px-2 py-1.5 flex items-center gap-1.5">
            <Sparkles size={11} /> Press <kbd className="bg-slate-100 border border-slate-200 rounded-sm px-1 mono">Ctrl + /</kbd> anywhere to reopen
          </div>
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
}
